import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'
import { CaretDown, CheckCircle, WarningCircle, XCircle } from 'phosphor-react'
import { useEffect, useState } from 'react'
import { ProjectForm } from './ProjectForm'
import { DialogModal } from '../../../../components/DialogModal'
import { Button } from '../../../../components/Button'
import { toast } from 'react-toastify'
import { LineProject } from './LineProject'
import { handlerType } from '../../../../enumns/handlerType'
import { type ProjectDomain } from '../../../../types/domain/project-domain'
import { useProjectDeleteMutation } from '../../../../services/hooks/projects/mutations/useProjectDeleteMutation'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useProjectToFileMutation } from '../../../../services/hooks/projects/mutations/useProjectToFileMutation'
import { invalidateQueriesList } from '../../../../utils/invalidateQueriesList'

type Props = {
  data: ProjectDomain | undefined
}

export const ActionSelect = ({ data }: Props) => {
  const queryClient = useQueryClient()
  const { mutate: deleteMutation, isLoading: isDeletingProject } =
    useProjectDeleteMutation()
  const { mutate: toFileMutation, isLoading: isArchivingProject } =
    useProjectToFileMutation()
  const navigate = useNavigate()

  const [open, setOpen] = useState<boolean>(false)
  const [editModal, setEditModal] = useState<boolean>(false)
  const [editTrigger, setEditTrigger] = useState<boolean>(false)
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [deleteTrigger, setDeleteTrigger] = useState<boolean>(false)
  const [archiveModal, setArchiveModal] = useState<boolean>(false)
  const [archiveTrigger, setArchiveTrigger] = useState<boolean>(false)
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [viewTrigger, setViewTrigger] = useState<boolean>(false)

  const closeEditModalHandler = () => {
    setEditTrigger(false)
    setEditModal(false)
  }

  const closeDeleteModalHandler = () => {
    setDeleteModal(false)
    setDeleteTrigger(false)
  }

  const closeArchiveModalHandler = () => {
    setArchiveModal(false)
    setArchiveTrigger(false)
  }

  const closeViewModalHandler = () => {
    setViewModal(false)
    setViewTrigger(false)
  }

  const editViewModalHandler = () => {
    closeViewModalHandler()
    setEditTrigger(true)
  }

  const confirmDeleteHandler = () => {
    if (data?.id) {
      deleteMutation(data?.id, {
        onSuccess() {
          invalidateQueriesList(queryClient, [
            ['projects'],
            ['project', data?.id],
            'my-tasks',
          ])

          closeDeleteModalHandler()
          navigate('/painel/projects')
          toast('Projeto excluído com sucesso!', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            closeButton: false,
            icon: (
              <CheckCircle
                size={48}
                className="mx-auto my-auto text-alert-success"
              />
            ),
            className: 'bg-green-50',
          })
        },
        onError(error: any) {
          toast.error(error, {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            closeButton: false,
            icon: (
              <XCircle size={48} className="mx-auto my-auto text-alert-error" />
            ),
            className: 'bg-red-50',
          })
        },
      })
    }
  }

  const confirmArchiveHandler = () => {
    if (data?.id) {
      toFileMutation(data?.id, {
        onSuccess() {
          queryClient.invalidateQueries(['projects'])
          queryClient.invalidateQueries([
            'filed-projects',
            {
              page: 1,
              take: 10,
              search: '',
            },
          ])
          closeArchiveModalHandler()
          navigate('/painel/projects')
          toast('Projeto arquivado com sucesso!', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            closeButton: false,
            icon: (
              <CheckCircle
                size={48}
                className="mx-auto my-auto text-alert-success"
              />
            ),
            className: 'bg-green-50',
          })
        },
        onError(error: any) {
          toast.error(error, {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            closeButton: false,
            icon: (
              <XCircle size={48} className="mx-auto my-auto text-alert-error" />
            ),
            className: 'bg-red-50',
          })
        },
      })
    }
  }

  useEffect(() => {
    if (!open && editTrigger) {
      setEditModal(true)
    }
  }, [editTrigger, open])

  useEffect(() => {
    if (!open && deleteTrigger) {
      setDeleteModal(true)
    }
  }, [deleteTrigger, open])

  useEffect(() => {
    if (!open && archiveTrigger) {
      setArchiveModal(true)
    }
  }, [archiveTrigger, open])

  useEffect(() => {
    if (!open && viewTrigger) {
      setViewModal(true)
    }
  }, [viewTrigger, open])

  return (
    <>
      <DropdownMenu.Root onOpenChange={setOpen} open={open}>
        <DropdownMenu.Trigger asChild>
          <button className="flex items-center justify-center gap-6 px-4 py-3 bg-transparent rounded-lg border-1 border-grey-500">
            <p className="text-sm font-normal  text-grey-800">
              Ações do projeto
            </p>
            <CaretDown
              size={20}
              className={clsx('transition transform ease-in-out', {
                'rotate-180': open,
              })}
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="z-50 items-center mt-1 bg-white border border-gray-300 rounded-lg shadow-lg w-72"
            align="end"
          >
            <DropdownMenu.DropdownMenuGroup className="p-6 space-y-6">
              <DropdownMenu.DropdownMenuItem>
                <button
                  className="cursor-pointer text-grey-800"
                  onClick={() => setViewTrigger(true)}
                >
                  Informações do projeto
                </button>
              </DropdownMenu.DropdownMenuItem>
              <DropdownMenu.DropdownMenuItem>
                <button
                  className="cursor-pointer text-grey-800"
                  onClick={() => setArchiveTrigger(true)}
                >
                  Arquivar projeto
                </button>
              </DropdownMenu.DropdownMenuItem>
              <DropdownMenu.DropdownMenuItem>
                <button
                  className="cursor-pointer text-grey-800"
                  onClick={() => setDeleteTrigger(true)}
                >
                  Excluir projeto
                </button>
              </DropdownMenu.DropdownMenuItem>
            </DropdownMenu.DropdownMenuGroup>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/* Edit Modal */}
      <ProjectForm
        isOpen={editModal}
        title={data?.name || ''}
        onClose={closeEditModalHandler}
        type={handlerType.EDIT}
        data={data}
      />

      {/* Delete Modal */}
      <DialogModal isOpen={deleteModal} onClose={closeDeleteModalHandler}>
        <div className="px-8 pb-6 overflow-y-auto scrollbar">
          <WarningCircle
            size={72}
            className="mx-auto mb-10 text-primary-light"
          />
          <p className="text-center text-grey-900">
            Você está prestes a excluir um projeto.
          </p>
          <p className="text-center mb-14 text-grey-900">Deseja continuar?</p>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeDeleteModalHandler}
              fullWidth
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={confirmDeleteHandler}
              isLoading={isDeletingProject}
              fullWidth
            >
              Continuar
            </Button>
          </div>
        </div>
      </DialogModal>

      {/* Archive Modal */}
      <DialogModal isOpen={archiveModal} onClose={closeArchiveModalHandler}>
        <div className="px-8 pb-6 overflow-y-auto scrollbar">
          <WarningCircle
            size={72}
            className="mx-auto mb-10 text-primary-light"
          />
          <p className="text-center text-grey-900">
            Você está prestes a arquivar um projeto
          </p>
          <p className="text-center mb-14 text-grey-900">Deseja continuar?</p>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeArchiveModalHandler}
              fullWidth
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={confirmArchiveHandler}
              isLoading={isArchivingProject}
              fullWidth
            >
              Continuar
            </Button>
          </div>
        </div>
      </DialogModal>

      {/* View Modal */}
      <DialogModal
        title={data?.name}
        isOpen={viewModal}
        onClose={closeViewModalHandler}
      >
        <div className="px-6 pt-8 pb-6 max-h-[calc(100vh_-_200px)] overflow-y-auto scrollbar">
          <div className="flex flex-col gap-6">
            <LineProject label="Nome do projeto" text={data?.name} />
            <LineProject label="Cliente" text={data?.client?.name} />
            <LineProject label="Solicitante" text={data?.requester?.name} />
            <LineProject
              label="Asset"
              array={data?.assets?.map((asset) => {
                return asset?.fullName
              })}
            />
            <LineProject
              label="Analista"
              array={data?.analysts.map(({ fullName }) => fullName)}
            />
            <LineProject
              label="Descritivo da demanda"
              text={data?.description}
            />
          </div>

          <div className="flex items-center mt-10">
            <Button
              type="button"
              variant="primary"
              onClick={editViewModalHandler}
              fullWidth
            >
              Editar
            </Button>
          </div>
        </div>
      </DialogModal>
    </>
  )
}
